import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

/**
 * CustomSelect - A reusable select component with custom styling
 *
 * @param {Object} props
 * @param {Array} props.options - Array of option objects with { value, label }
 * @param {string|number} props.value - Currently selected value
 * @param {Function} props.onChange - Callback function (value) => void
 * @param {string} props.placeholder - Placeholder text when no value selected
 * @param {string} props.allOptionLabel - Label for "all" option (e.g., "All Academies")
 * @param {Function} props.getDisplayValue - Optional function to get display text for selected value
 * @param {string} props.allValue - Value for "all" option (default: "all")
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.minWidth - Minimum width in pixels (default: 180)
 */
const CustomSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  allOptionLabel,
  getDisplayValue,
  allValue = "all",
  className = "",
  minWidth = 180,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get display text for selected value
  const getDisplayText = () => {
    if (value === allValue || value === "all") {
      return placeholder;
    }
    if (getDisplayValue) {
      return getDisplayValue(value);
    }
    const selectedOption = options.find(
      (opt) => opt.value === value || opt.id === value
    );
    return selectedOption?.label || selectedOption?.name || placeholder;
  };

  // Handle option selection
  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={selectRef}
      className={`relative ${className}`}
      style={{ minWidth: `${minWidth}px` }}
    >
      {/* Trigger Button */}
      <div
        className="flex h-9 w-full items-center justify-between rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm shadow-sm transition-colors hover:bg-zinc-50 focus:outline-none focus:ring-1 focus:ring-zinc-950 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-zinc-700">{getDisplayText()}</span>
        <ChevronDown
          className="h-4 w-4 text-zinc-500 opacity-50"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease-in-out",
          }}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 top-[calc(100%+4px)] z-[9999] min-w-full rounded-md border border-zinc-200 bg-white p-1 text-zinc-950 shadow-md">
          <div className="flex flex-col gap-1">
            {/* All Option */}
            {allOptionLabel && (
              <div
                className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${
                  value === allValue || value === "all"
                    ? "bg-zinc-100 text-zinc-900"
                    : "hover:bg-zinc-100"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(allValue);
                }}
              >
                {allOptionLabel}
              </div>
            )}

            {/* Options List */}
            {options.map((option) => {
              const optionValue =
                option.value ||
                option.id ||
                option.academyId ||
                option._id ||
                option.departmentId ||
                option.divisionId;
              const optionLabel = option.label || option.name;
              const isSelected = value === optionValue;

              return (
                <div
                  key={optionValue}
                  className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${
                    isSelected
                      ? "bg-zinc-100 text-zinc-900"
                      : "hover:bg-zinc-100"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(optionValue);
                  }}
                >
                  {optionLabel}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
